import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const PlanConfigPanel = ({ clients, planConfig, onUpdateConfig, onSavePlan, isSaving, weekDays }) => {
  const [errors, setErrors] = useState({});

  const totalExercises = weekDays?.reduce((acc, d) => acc + d?.exercises?.length, 0);
  const activeDays = weekDays?.filter(d => !d?.isRest && d?.exercises?.length > 0)?.length;

  const validate = () => {
    const errs = {};
    if (!planConfig?.name?.trim()) errs.name = 'Plan name is required';
    if (!planConfig?.clientId) errs.clientId = 'Select a client';
    if (!planConfig?.startDate) errs.startDate = 'Start date is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = () => {
    if (validate()) onSavePlan();
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    borderRadius: '12px',
    padding: '8px 12px',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
  };

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '6px',
    display: 'block',
    fontFamily: "'Inter Tight', 'Inter', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)' }}>
          <Icon name="Settings" size={16} style={{ color: '#FF6B00' }} />
        </div>
        <h2 className="text-base font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Plan Settings</h2>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)' }}>
            <p className="text-2xl font-extrabold" style={{ color: '#00D4FF', fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: '0 0 10px #00D4FF66' }}>{totalExercises}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Exercises</p>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(170,255,0,0.06)', border: '1px solid rgba(170,255,0,0.2)' }}>
            <p className="text-2xl font-extrabold" style={{ color: '#AAFF00', fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: '0 0 10px #AAFF0066' }}>{activeDays}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Active Days</p>
          </div>
        </div>

        {/* Client Selection */}
        <div>
          <label style={labelStyle}>Assign to Client *</label>
          <select
            value={planConfig?.clientId || ''}
            onChange={e => onUpdateConfig('clientId', e?.target?.value)}
            style={{ ...inputStyle, borderColor: errors?.clientId ? 'rgba(255,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
          >
            <option value="" style={{ background: '#1a1a1b' }}>Select client...</option>
            {clients?.map(c => (
              <option key={c?.id} value={c?.id} style={{ background: '#1a1a1b' }}>{c?.client?.fullName || c?.client?.email}</option>
            ))}
          </select>
          {errors?.clientId && <p className="text-xs mt-1" style={{ color: '#ff4444' }}>{errors?.clientId}</p>}
        </div>

        {/* Plan Name */}
        <div>
          <label style={labelStyle}>Plan Name *</label>
          <input
            value={planConfig?.name || ''}
            onChange={e => onUpdateConfig('name', e?.target?.value)}
            placeholder="e.g. 4-Week Hypertrophy Plan"
            style={{ ...inputStyle, borderColor: errors?.name ? 'rgba(255,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
          />
          {errors?.name && <p className="text-xs mt-1" style={{ color: '#ff4444' }}>{errors?.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>Description</label>
          <textarea
            value={planConfig?.description || ''}
            onChange={e => onUpdateConfig('description', e?.target?.value)}
            placeholder="Plan overview and goals..."
            rows={3}
            style={{ ...inputStyle, resize: 'none' }}
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={labelStyle}>Start Date *</label>
            <input
              type="date"
              value={planConfig?.startDate || ''}
              onChange={e => onUpdateConfig('startDate', e?.target?.value)}
              style={{ ...inputStyle, borderColor: errors?.startDate ? 'rgba(255,68,68,0.5)' : 'rgba(255,255,255,0.1)' }}
            />
            {errors?.startDate && <p className="text-xs mt-1" style={{ color: '#ff4444' }}>{errors?.startDate}</p>}
          </div>
          <div>
            <label style={labelStyle}>End Date</label>
            <input
              type="date"
              value={planConfig?.endDate || ''}
              onChange={e => onUpdateConfig('endDate', e?.target?.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Validation hints */}
        {totalExercises === 0 && (
          <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.2)' }}>
            <Icon name="AlertTriangle" size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#FF6B00' }} />
            <p className="text-xs" style={{ color: '#FF6B00' }}>Add exercises to at least one day before saving</p>
          </div>
        )}

        {totalExercises > 0 && activeDays > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(170,255,0,0.06)', border: '1px solid rgba(170,255,0,0.2)' }}>
            <Icon name="CheckCircle" size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#AAFF00' }} />
            <p className="text-xs" style={{ color: '#AAFF00' }}>{activeDays} training days with {totalExercises} exercises ready</p>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <button
          data-tour="save-plan-btn"
          onClick={handleSave}
          disabled={isSaving || totalExercises === 0}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all"
          style={{
            fontFamily: "'Inter Tight', 'Inter', sans-serif",
            background: isSaving || totalExercises === 0 ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #AAFF00, #88cc00)',
            color: isSaving || totalExercises === 0 ? 'rgba(255,255,255,0.25)' : '#0A0A0B',
            border: isSaving || totalExercises === 0 ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(170,255,0,0.4)',
            boxShadow: isSaving || totalExercises === 0 ? 'none' : '0 0 16px rgba(170,255,0,0.25)',
            cursor: isSaving || totalExercises === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: 'rgba(255,255,255,0.6)' }} />
              Saving Plan...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} />
              Save & Assign Plan
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PlanConfigPanel;
